/**
 * Login Page — Unified design (mobile-first, consistent across all screen sizes)
 * Navy gradient background + centred white card
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import '../styles/Login.css';

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="input-icon">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="input-icon">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23" width="18" height="18"
    aria-hidden="true" style={{ flexShrink: 0 }}>
    <rect x="1"  y="1"  width="10" height="10" fill="#f25022" />
    <rect x="12" y="1"  width="10" height="10" fill="#7fba00" />
    <rect x="1"  y="12" width="10" height="10" fill="#00a4ef" />
    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function Login() {
  const { loginSSO, loginLocal, isAuthenticated, isLoading } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [localError, setLocalError]     = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake]               = useState(false);
  const [userFocused, setUserFocused]   = useState(false);
  const [passFocused, setPassFocused]   = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLocalError('');
    setLocalLoading(true);
    try {
      await loginLocal(username.trim(), password);
    } catch (err) {
      setLocalError(err.message || 'Invalid username or password.');
      triggerShake();
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <div className="lp-root">
      {/* Animated background blobs */}
      <div className="lp-blob lp-blob-1" />
      <div className="lp-blob lp-blob-2" />
      <div className="lp-blob lp-blob-3" />

      {/* Centred card */}
      <div className={`lp-card${shake ? ' lp-shake' : ''}`}>

        {/* Logo + branding */}
        <div className="lp-header">
          <div className="lp-header-top">
            {/* <div className="lp-logo-wrap"> */}
              <img src="/logo.png" alt="iAssess" className="lp-logo" />
            {/* </div> */}
            <div className="lp-header-text">
              <h1 className="lp-brand-name">iAssess</h1>
              <p className="lp-tagline">AI-Powered Skill Assessment Platform</p>
            </div>
          </div>
        </div>

        <div className="lp-divider" />

        <h2 className="lp-form-title">Welcome back</h2>
        <p className="lp-form-subtitle">Sign in to your account to continue</p>

        {/* ── Username / Password form ───────────────────────────── */}
        <form className="lp-form" onSubmit={handleLocalLogin} noValidate>

          {/* Username */}
          <div className={`lp-field${userFocused || username ? ' lp-field--active' : ''}`}>
            <span className="lp-field-icon"><UserIcon /></span>
            <input
              id="lp-username"
              type="text"
              className="lp-input"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onFocus={() => setUserFocused(true)}
              onBlur={() => setUserFocused(false)}
              autoComplete="username"
              disabled={localLoading}
              required
            />
          </div>

          {/* Password */}
          <div className={`lp-field${passFocused || password ? ' lp-field--active' : ''}`}>
            <span className="lp-field-icon"><LockIcon /></span>
            <input
              id="lp-password"
              type={showPassword ? 'text' : 'password'}
              className="lp-input lp-input--pw"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onFocus={() => setPassFocused(true)}
              onBlur={() => setPassFocused(false)}
              autoComplete="current-password"
              disabled={localLoading}
              required
            />
          </div>

          {/* Error */}
          {localError && (
            <div className="lp-error" role="alert">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                width="15" height="15" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {localError}
            </div>
          )}

          {/* Sign In */}
          <button
            type="submit"
            className="lp-btn lp-btn--primary"
            disabled={localLoading || !username.trim() || !password}
          >
            {localLoading ? (
              <><span className="lp-spinner" />Signing in…</>
            ) : 'Sign In'}
          </button>
        </form>

        {/* OR divider */}
        <div className="lp-or">
          <span className="lp-or-line" />
          <span className="lp-or-text">or continue with</span>
          <span className="lp-or-line" />
        </div>

        {/* SSO */}
        <button
          className="lp-btn lp-btn--sso"
          onClick={loginSSO}
          disabled
          aria-label="Sign in with SSO (coming soon)"
          title="SSO login is currently disabled"
        >
          {isLoading ? (
            <><span className="lp-spinner lp-spinner--dark" />Redirecting…</>
          ) : (
            <><MicrosoftIcon />Sign in with SSO</>
          )}
        </button>

        <p className="lp-footer">
          By signing in, you agree to your organisation's usage policies.
        </p>
      </div>
    </div>
  );
}
